import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import PageTitle from '../components/PageTitle';

const STEPS = ['Return Details', 'Sales & Tax', 'Purchases & ITC', 'Documents & Submit'];

const RETURN_TYPES = [
  { type: 'GSTR-1',  desc: 'Outward supplies — monthly/quarterly', period: 'monthly' },
  { type: 'GSTR-3B', desc: 'Monthly summary return — tax payment',  period: 'monthly' },
  { type: 'GSTR-9',  desc: 'Annual return for regular taxpayers',   period: 'annual'  },
];

const MONTHS = ['April','May','June','July','August','September','October','November','December','January','February','March'];
const YEARS  = ['2025-26','2024-25','2023-24'];
const FY     = ['FY 2024-25','FY 2023-24','FY 2022-23'];

const INIT = {
  gstin: '', businessName: '', returnType: '', filingPeriod: '',
  email: '', phone: '',
  totalTurnover: '', taxableSupplies: '', exemptSupplies: '', totalTaxCollected: '',
  totalPurchases: '', itcAvailable: '', itcUtilized: '', taxPayable: '', taxPaid: '',
  remarks: '',
};

function Field({ label, name, type='text', placeholder, required, span2, value, onChange, error, hint }) {
  return (
    <div className={`field${span2 ? ' span2' : ''}`}>
      <label>{label}{required && <span className="req">*</span>}</label>
      <input name={name} type={type} placeholder={placeholder}
        value={value} onChange={onChange} className={error ? 'err' : ''} />
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="err-msg">⚠ {error}</span>}
    </div>
  );
}

// Single file upload
function FileZone({ label, name, hint, file, onChange }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className={`file-zone ${file ? 'has-file' : ''}`}>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
          onChange={e => onChange(name, e.target.files[0])} />
        <div style={{ fontSize: '1.4rem', marginBottom: '.2rem' }}>{file ? '✅' : '📎'}</div>
        <div className="file-zone-label">{file ? 'Selected' : 'Click to upload'}</div>
        {file ? <div className="file-name">📄 {file.name}</div> : <div className="file-zone-sub">{hint}</div>}
      </div>
    </div>
  );
}

// Multi-file upload — supports adding multiple files, removing individual ones
function MultiFileZone({ label, name, hint, accept, files, onChange }) {
  const handleAdd = e => {
    const newFiles = Array.from(e.target.files);
    const oversized = newFiles.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length) { alert(`${oversized.length} file(s) exceed 10MB and were skipped.`); return; }
    onChange(name, [...(files || []), ...newFiles]);
    e.target.value = ''; // reset so same file can be re-added
  };
  const removeFile = idx => onChange(name, (files || []).filter((_, i) => i !== idx));
  const hasFiles = files && files.length > 0;

  return (
    <div className="field" style={{ gridColumn: 'span 2' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
        {label}
        <span style={{ background: 'var(--bluel)', color: 'var(--blue)', fontSize: '.68rem', fontWeight: 700, padding: '.1rem .45rem', borderRadius: 4 }}>
          Multiple allowed
        </span>
      </label>

      {/* Existing files list */}
      {hasFiles && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem', marginBottom: '.6rem' }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 8, padding: '.45rem .75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.82rem' }}>
                <span>📄</span>
                <span style={{ color: 'var(--green)', fontWeight: 500 }}>{f.name}</span>
                <span style={{ color: 'var(--muted)', fontSize: '.72rem' }}>({(f.size / 1024).toFixed(0)} KB)</span>
              </div>
              <button onClick={() => removeFile(i)}
                style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: '.85rem', fontWeight: 700, padding: '.1rem .35rem' }}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add more button */}
      <label style={{ cursor: 'pointer' }}>
        <div style={{ border: `2px dashed ${hasFiles ? 'var(--green)' : 'var(--border)'}`, borderRadius: 10, padding: '1rem', textAlign: 'center', background: hasFiles ? 'rgba(26,107,67,.04)' : 'var(--cream)', transition: 'all .2s' }}
          onMouseOver={e => e.currentTarget.style.borderColor = 'var(--gold)'}
          onMouseOut={e => e.currentTarget.style.borderColor = hasFiles ? 'var(--green)' : 'var(--border)'}>
          <input type="file" accept={accept || '*'} multiple onChange={handleAdd} style={{ display: 'none' }} />
          <div style={{ fontSize: '1.3rem', marginBottom: '.2rem' }}>➕</div>
          <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--navy)' }}>
            {hasFiles ? `Add more files (${files.length} added)` : 'Click to add files'}
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: '.15rem' }}>{hint}</div>
        </div>
      </label>
    </div>
  );
}

export default function GstReturnForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep]       = useState(1);
  const [data, setData]       = useState({
    ...INIT,
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [files, setFiles]         = useState({});      // single files
  const [multiFiles, setMultiFiles] = useState({         // multi-file arrays
    docInvoices: [],
    docOther: [],
  });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(null);

  const handle = e => {
    const { name, value } = e.target;
    const v = name === 'gstin' ? value.toUpperCase() : value;
    setData(d => ({ ...d, [name]: v }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const handleFile = (name, file) => {
    if (file && file.size > 10 * 1024 * 1024) { alert('Max 10MB per file'); return; }
    setFiles(f => ({ ...f, [name]: file }));
  };

  const handleMultiFile = (name, fileArray) => {
    setMultiFiles(f => ({ ...f, [name]: fileArray }));
  };

  const validate = () => {
    const e = {};
    if (step === 1) {
      if (!data.gstin.trim() || !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/.test(data.gstin))
        e.gstin = 'Invalid GSTIN (e.g. 27AAPFU0939F1ZV)';
      if (!data.businessName.trim()) e.businessName = 'Required';
      if (!data.returnType)          e.returnType   = 'Select return type';
      if (!data.filingPeriod.trim()) e.filingPeriod = 'Required';
      if (!data.email.trim() || !/^[\w.+-]+@[\w-]+\.[a-z]{2,}$/i.test(data.email)) e.email = 'Invalid email';
      if (!data.phone.trim() || !/^[6-9]\d{9}$/.test(data.phone)) e.phone = 'Invalid mobile';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) { setStep(s => s + 1); window.scrollTo(0, 0); } };
  const prev = () => { setStep(s => s - 1); window.scrollTo(0, 0); };

  const submit = async () => {
    setLoading(true); setApiError('');
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => { if (v) fd.append(k, v); });
      Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });
      // Multi-files: append each with same field name (backend receives as array)
      Object.entries(multiFiles).forEach(([k, arr]) => {
        if (arr && arr.length) arr.forEach(f => fd.append(k, f));
      });
      const { data: res } = await api.post('/api/gst-return/submit', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(res);
    } catch (err) {
      setApiError(err.response?.data?.error || 'Submission failed');
    } finally { setLoading(false); }
  };

  // Period selector based on return type
  const selectedType = RETURN_TYPES.find(t => t.type === data.returnType);

  if (success) {
    return (
      <>
        <PageTitle title="GST Return Submitted" />
        <div className="page" style={{ maxWidth: 580 }}>
          <div className="card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <div style={{ width: 80, height: 80, background: 'var(--greenl)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '2rem' }}>✅</div>
            <h2 style={{ color: 'var(--navy)', marginBottom: '.5rem' }}>Return Submitted!</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>Application #{success.id} received by our CA team.</p>
            <div style={{ background: 'var(--bluel)', color: 'var(--blue)', padding: '.5rem 1.5rem', borderRadius: 8, fontWeight: 700, display: 'inline-block', marginBottom: '1rem' }}>
              {success.returnType} — {success.filingPeriod}
            </div>
            <div style={{ background: 'var(--cream)', borderRadius: 12, padding: '1.25rem', textAlign: 'left', marginBottom: '2rem' }}>
              <div style={{ fontSize: '.78rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--navy)', marginBottom: '.75rem' }}>What happens next</div>
              {['CA team reviews your documents & figures', 'Any discrepancies communicated to you', 'Return filed on GST portal on your behalf', 'ARN sent to your registered email & mobile', 'Download filed return acknowledgement from dashboard'].map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '.75rem', marginBottom: '.5rem', fontSize: '.85rem', color: 'var(--muted)', alignItems: 'flex-start' }}>
                  <div style={{ width: 20, height: 20, background: 'var(--navy)', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                  {s}
                </div>
              ))}
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard →</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle title="GST Return Filing" />
      <div className="page-hero">
        <div className="page-hero-label">GST Returns</div>
        <h1>File Your GST Return</h1>
        <p>GSTR-1, GSTR-3B, GSTR-9 — handled by our CA team</p>
      </div>

      {/* Progress */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '1.5rem 2rem' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div className="progress-track">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const state = n < step ? 'done' : n === step ? 'active' : 'pending';
              return (
                <div key={label} className={`pt-step ${state}`}>
                  <div className="pt-circle">{state === 'done' ? '✓' : n}</div>
                  <div className="pt-label">{label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="page">
        <div className="card">
          {apiError && <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>⚠ {apiError}</div>}

          {/* ── Step 1: Return Details ── */}
          {step === 1 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 1 of 4</div>
                <h2 className="step-title">Return Details</h2>
                <p className="step-desc">Basic information about your GST return.</p>
              </div>

              <div className="form-grid">
                <Field name="gstin" label="GSTIN" required placeholder="27AAPFU0939F1ZV"
                  maxLength={15} value={data.gstin} onChange={handle} error={errors.gstin}
                  hint="15-character GST Identification Number" />
                <Field name="businessName" label="Business Name" required placeholder="Sharma Enterprises"
                  value={data.businessName} onChange={handle} error={errors.businessName} />
                <Field name="email" label="Email Address" type="email" required placeholder="you@email.com"
                  value={data.email} onChange={handle} error={errors.email} />
                <Field name="phone" label="Mobile Number" required placeholder="9876543210" maxLength={10}
                  value={data.phone} onChange={handle} error={errors.phone} />
              </div>

              {/* Return type selector */}
              <div className="field" style={{ marginTop: '1rem' }}>
                <label>Return Type<span className="req">*</span></label>
                {errors.returnType && <span className="err-msg">⚠ {errors.returnType}</span>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '.75rem', marginTop: '.4rem' }}>
                  {RETURN_TYPES.map(t => (
                    <div key={t.type}
                      onClick={() => { handle({ target: { name: 'returnType', value: t.type } }); setData(d => ({ ...d, filingPeriod: '' })); }}
                      style={{ border: `2px solid ${data.returnType === t.type ? 'var(--navy)' : 'var(--border)'}`, background: data.returnType === t.type ? 'var(--bluel)' : 'var(--cream)', borderRadius: 10, padding: '1rem', cursor: 'pointer', transition: 'all .2s' }}>
                      <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: '.3rem' }}>{t.type}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--muted)', lineHeight: 1.4 }}>{t.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dynamic period selector */}
              {data.returnType && (
                <div className="field" style={{ marginTop: '1rem', maxWidth: 320 }}>
                  <label>Filing Period<span className="req">*</span></label>
                  {data.returnType === 'GSTR-9' ? (
                    <select name="filingPeriod" value={data.filingPeriod} onChange={handle} className={errors.filingPeriod ? 'err' : ''}>
                      <option value="">Select financial year...</option>
                      {FY.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  ) : (
                    <div style={{ display: 'flex', gap: '.75rem' }}>
                      <select onChange={e => {
                        const month = e.target.value;
                        const yr = data.filingPeriod?.split(' ')[1] || '';
                        if (month) setData(d => ({ ...d, filingPeriod: `${month} ${yr}`.trim() }));
                      }} style={{ flex: 1 }}>
                        <option value="">Month...</option>
                        {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <select onChange={e => {
                        const yr = e.target.value;
                        const month = data.filingPeriod?.split(' ')[0] || '';
                        if (yr) setData(d => ({ ...d, filingPeriod: `${month} ${yr}`.trim() }));
                      }} style={{ flex: 1 }}>
                        <option value="">Year...</option>
                        {['2026','2025','2024','2023'].map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  )}
                  {data.filingPeriod && <span className="hint">Period: {data.filingPeriod}</span>}
                  {errors.filingPeriod && <span className="err-msg">⚠ {errors.filingPeriod}</span>}
                </div>
              )}
            </>
          )}

          {/* ── Step 2: Sales Summary ── */}
          {step === 2 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 2 of 4</div>
                <h2 className="step-title">Sales & Output Tax Summary</h2>
                <p className="step-desc">Enter figures from your sales register for {data.filingPeriod}. All amounts in ₹.</p>
              </div>

              <div className="alert alert-info" style={{ marginBottom: '1.5rem', fontSize: '.85rem' }}>
                ℹ️ Enter approximate figures — our CA team will verify and reconcile with your documents before filing.
              </div>

              <div className="form-grid">
                <Field name="totalTurnover" label="Total Turnover / Gross Sales (₹)" placeholder="500000"
                  value={data.totalTurnover} onChange={handle} hint="All sales including exempt" />
                <Field name="taxableSupplies" label="Taxable Supplies (₹)" placeholder="450000"
                  value={data.taxableSupplies} onChange={handle} hint="Sales attracting GST" />
                <Field name="exemptSupplies" label="Exempt / Nil Rated Supplies (₹)" placeholder="50000"
                  value={data.exemptSupplies} onChange={handle} />
                <Field name="totalTaxCollected" label="Total Output GST Collected (₹)" placeholder="81000"
                  value={data.totalTaxCollected} onChange={handle} hint="CGST + SGST + IGST collected" />
              </div>

              {/* Quick summary */}
              {data.totalTurnover && (
                <div style={{ marginTop: '1.5rem', background: 'var(--navy)', borderRadius: 12, padding: '1.25rem 1.5rem' }}>
                  <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--goldt)', fontWeight: 600, marginBottom: '1rem' }}>Sales Summary</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', textAlign: 'center' }}>
                    {[['Total Turnover', data.totalTurnover], ['Taxable Supplies', data.taxableSupplies], ['Output GST', data.totalTaxCollected]].map(([l, v]) => (
                      <div key={l}>
                        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', color: 'var(--goldt)' }}>
                          ₹{Number(v || 0).toLocaleString('en-IN')}
                        </div>
                        <div style={{ fontSize: '.7rem', color: 'rgba(255,255,255,.4)', marginTop: 2 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Step 3: Purchases & ITC ── */}
          {step === 3 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 3 of 4</div>
                <h2 className="step-title">Purchases & Input Tax Credit</h2>
                <p className="step-desc">Enter purchase figures and ITC details for {data.filingPeriod}.</p>
              </div>

              <div className="form-grid">
                <Field name="totalPurchases" label="Total Purchases / Inward Supplies (₹)" placeholder="300000"
                  value={data.totalPurchases} onChange={handle} />
                <Field name="itcAvailable" label="Input Tax Credit (ITC) Available (₹)" placeholder="54000"
                  value={data.itcAvailable} onChange={handle} hint="GST paid on purchases" />
                <Field name="itcUtilized" label="ITC Utilized (₹)" placeholder="54000"
                  value={data.itcUtilized} onChange={handle} hint="ITC set off against output tax" />
                <Field name="taxPayable" label="Net Tax Payable (₹)" placeholder="27000"
                  value={data.taxPayable} onChange={handle} hint="Output GST − ITC utilized" />
                <Field name="taxPaid" label="Tax Already Paid via Challan (₹)" placeholder="27000"
                  value={data.taxPaid} onChange={handle} hint="PMT-06 / challan payment" />
              </div>

              {/* Net liability summary */}
              {(data.taxPayable || data.taxPaid) && (
                <div style={{ marginTop: '1.5rem', background: 'var(--navy)', borderRadius: 12, padding: '1.25rem 1.5rem' }}>
                  <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--goldt)', fontWeight: 600, marginBottom: '1rem' }}>Tax Liability Summary</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', textAlign: 'center' }}>
                    {[
                      ['ITC Available', data.itcAvailable],
                      ['Net Tax Payable', data.taxPayable],
                      ['Tax Paid', data.taxPaid]
                    ].map(([l, v]) => (
                      <div key={l}>
                        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', color: 'var(--goldt)' }}>
                          ₹{Number(v || 0).toLocaleString('en-IN')}
                        </div>
                        <div style={{ fontSize: '.7rem', color: 'rgba(255,255,255,.4)', marginTop: 2 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                  {data.taxPayable && data.taxPaid && (
                    <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '.82rem', color: Number(data.taxPaid) >= Number(data.taxPayable) ? '#5DCAA5' : '#F09595' }}>
                      {Number(data.taxPaid) >= Number(data.taxPayable)
                        ? '✅ Tax fully paid — no additional payment needed'
                        : `⚠️ Balance payable: ₹${(Number(data.taxPayable) - Number(data.taxPaid)).toLocaleString('en-IN')}`}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Step 4: Documents & Review ── */}
          {step === 4 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 4 of 4</div>
                <h2 className="step-title">Upload Documents & Submit</h2>
                <p className="step-desc">Upload supporting documents. PDF, Excel, CSV accepted. Max 10MB each.</p>
              </div>

              <div className="form-grid">
                <FileZone name="docSalesRegister" label="Sales Register / Outward Supply Register"
                  hint="Excel/PDF of sales for the period" file={files.docSalesRegister} onChange={handleFile} />
                <FileZone name="docPurchaseRegister" label="Purchase Register / Inward Supply Register"
                  hint="Excel/PDF of purchases for the period" file={files.docPurchaseRegister} onChange={handleFile} />
                <FileZone name="docChallan" label="Tax Payment Challan (PMT-06)"
                  hint="Proof of tax already paid" file={files.docChallan} onChange={handleFile} />
                <MultiFileZone
                  name="docInvoices"
                  label="Invoices"
                  hint="Add as many invoices as needed — any format (PDF, JPG, Excel, etc.)"
                  accept="*"
                  files={multiFiles.docInvoices}
                  onChange={handleMultiFile}
                />
                <MultiFileZone
                  name="docOther"
                  label="Other Documents"
                  hint="Any other supporting documents — any format, multiple files allowed"
                  accept="*"
                  files={multiFiles.docOther}
                  onChange={handleMultiFile}
                />
              </div>

              <div className="field" style={{ marginTop: '1rem' }}>
                <label>Additional Remarks</label>
                <textarea name="remarks" rows={3}
                  placeholder="Any special instructions — e.g. reverse charge entries, credit notes, amendments..."
                  value={data.remarks} onChange={handle} />
              </div>

              {/* Review summary */}
              <div style={{ marginTop: '1.5rem', background: 'var(--cream)', borderRadius: 12, padding: '1.25rem' }}>
                <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.75rem' }}>Review</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.4rem' }}>
                  {[['GSTIN', data.gstin], ['Business', data.businessName], ['Return Type', data.returnType], ['Period', data.filingPeriod], ['Turnover', data.totalTurnover ? `₹${Number(data.totalTurnover).toLocaleString('en-IN')}` : '—'], ['Tax Payable', data.taxPayable ? `₹${Number(data.taxPayable).toLocaleString('en-IN')}` : '—']].map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem 0', borderBottom: '1px solid var(--border)', fontSize: '.83rem' }}>
                      <span style={{ color: 'var(--muted)' }}>{l}</span>
                      <span style={{ fontWeight: 500, color: 'var(--navy)' }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="alert alert-info" style={{ marginTop: '1rem', fontSize: '.83rem' }}>
                ⚠️ By submitting, you authorise MyTaxNiti to file this GST return on your behalf.
              </div>
            </>
          )}

          <div className="btn-row">
            {step > 1 ? <button className="btn btn-outline" onClick={prev}>← Back</button> : <span />}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>Step {step} of 4</span>
              {step < 4
                ? <button className="btn btn-primary" onClick={next}>Continue →</button>
                : <button className="btn btn-gold" onClick={submit} disabled={loading}>
                    {loading ? <><span className="spinner" /> Submitting...</> : '🚀 Submit Return'}
                  </button>
              }
            </div>
          </div>
        </div>
      </div>
    </>
  );
}